import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEWS = [
  { key: "parasara", label: "Parasara (BPHS)" },
  { key: "bv_raman", label: "BV Raman" },
  { key: "jaimini", label: "Jaimini" },
  { key: "alan_leo", label: "Alan Leo" },
];

function md(text) {
  if (!text) return null;
  return text.split("\n").map((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) return <h3 key={k} className="font-heading text-lg text-[#D4AF37] mt-4 mb-2">{l.slice(3)}</h3>;
    if (l.startsWith("# ")) return <h2 key={k} className="font-heading text-xl text-[#E6E1D6] mt-4 mb-2">{l.slice(2)}</h2>;
    if (l.startsWith("- ") || l.startsWith("* ")) return <li key={k} className="text-sm text-[#E6E1D6] ml-6 list-disc">{l.slice(2)}</li>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-sm text-[#E6E1D6] leading-relaxed font-light">{l}</p>;
  });
}

export default function ChartViewsPanel({ profileId }) {
  const { lang } = useLang();
  const [view, setView] = useState("parasara");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // BPHS 99-chapter drill-down (only when view=parasara)
  const [chapters, setChapters] = useState([]);
  const [chapterKey, setChapterKey] = useState(null);
  const [chapterData, setChapterData] = useState(null);
  const [chapterLoading, setChapterLoading] = useState(false);

  // Alan Leo · Key-of-Destiny life-arc phases (only when view=alan_leo)
  const [phases, setPhases] = useState([]);
  const [phaseKey, setPhaseKey] = useState(null);
  const [phaseData, setPhaseData] = useState(null);
  const [phaseLoading, setPhaseLoading] = useState(false);

  const load = useCallback(async (v) => {
    const useView = v ?? view;
    setLoading(true); setData(null);
    try { setData(await api.chartView(profileId, useView)); }
    catch (e) {
      console.error("chart view failed:", e);
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setLoading(false);
  }, [profileId, view]);
  useEffect(() => {
    load("parasara");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, lang]);

  // Load chapter index once
  useEffect(() => {
    axios.get(`${API}/bphs/chapters`)
      .then(r => setChapters(r.data.chapters || []))
      .catch(() => {});
  }, []);

  const openChapter = useCallback(async (key) => {
    setChapterKey(key); setChapterData(null); setChapterLoading(true);
    try {
      const r = await axios.get(`${API}/profiles/${profileId}/bphs-chapter`,
        { params: { chapter: key, lang } });
      setChapterData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chapter load failed");
    } finally { setChapterLoading(false); }
  }, [profileId, lang]);

  const openPhase = useCallback(async (key) => {
    setPhaseKey(key); setPhaseData(null); setPhaseLoading(true);
    try {
      const r = await axios.get(`${API}/profiles/${profileId}/alan-leo-phase`,
        { params: { phase: key, lang } });
      setPhaseData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Phase load failed");
    } finally { setPhaseLoading(false); }
  }, [profileId, lang]);

  return (
    <div className="space-y-6" data-testid="chart-views-panel">
      <div className="flex gap-2 flex-wrap">
        {VIEWS.map((v) => (
          <Button key={v.key} data-testid={`chartview-${v.key}`}
                  onClick={() => { setView(v.key); load(v.key); }}
                  variant={view === v.key ? "default" : "ghost"}
                  className={view === v.key
                    ? "rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]"
                    : "rounded-sm border border-[#2A2D35] text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent"}>
            {v.label}
          </Button>
        ))}
      </div>

      {loading && <div className="text-[#A19E95] font-mono text-sm">Distilling {view} reading… (~60s first time)</div>}

      {data && (
        <>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
            {data.view_name} · Lens on the same natal data
          </div>
          {data.chara_karakas && (
            <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#A8896C] font-mono mb-3">Chara Karakas (Jaimini)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.chara_karakas).map(([title, val]) => (
                  <div key={title} className="border border-[#2A2D35] p-3 bg-[#0F1115]">
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{title}</div>
                    <div className="font-heading text-lg text-[#E6E1D6] mt-1">{val.planet}</div>
                    <div className="text-[10px] font-mono text-[#A19E95]">{val.deg_in_sign}°</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="border border-[#2A2D35] bg-[#181A1F] p-6" data-testid={`chartview-narrative-${data.view}`}>
            {md(data.narrative)}
          </div>

          {/* BPHS 99-chapter drill-down — only for Parasara view */}
          {view === "parasara" && chapters.length > 0 && (
            <div className="border border-[#D4AF37]/40 bg-[#151821] p-5" data-testid="bphs-chapter-index">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={14} className="text-[#D4AF37]" />
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
                  BPHS Chapter Drill-Down · {chapters.length} topics
                </div>
              </div>
              <div className="text-[10px] text-[#6E6B64] font-mono mb-4">
                Jump into any chapter of Brihat Parasara Hora Shastra scoped to THIS chart.
                Every reading is grounded in the classical rules of that chapter and delivered in a storyteller cadence.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {chapters.map((c) => (
                  <button key={c.key} onClick={() => openChapter(c.key)}
                          data-testid={`bphs-chapter-${c.key}`}
                          className={`text-left border px-3 py-2.5 rounded-sm transition-colors text-xs font-mono
                            ${chapterKey === c.key
                              ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                              : "border-[#2A2D35] text-[#A19E95] hover:border-[#D4AF37]/40 hover:text-[#E6E1D6]"}`}>
                    <div className="font-medium leading-tight">{c.label}</div>
                    <div className="text-[9px] text-[#6E6B64] mt-1">{c.bphs_ref}</div>
                  </button>
                ))}
              </div>

              {chapterLoading && (
                <div className="mt-6 flex items-center gap-2 text-[#A19E95] font-mono text-sm">
                  <Loader2 size={14} className="animate-spin" /> Distilling this BPHS chapter reading…
                </div>
              )}

              {chapterData && !chapterLoading && (
                <div className="mt-6 border border-[#D4AF37]/30 bg-[#0F1115] p-5"
                     data-testid={`bphs-chapter-reading-${chapterData.chapter}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#A8896C] font-mono mb-1">
                    {chapterData.bphs_ref}
                  </div>
                  <div className="font-heading text-xl text-[#D4AF37] mb-4">{chapterData.label}</div>
                  {md(chapterData.narrative)}
                </div>
              )}
            </div>
          )}

          {/* Alan Leo · Key-of-Destiny 7-phase drill-down — only for Alan Leo view */}
          {view === "alan_leo" && phases.length > 0 && (
            <div className="border border-[#D4AF37]/40 bg-[#151821] p-5" data-testid="alan-leo-phases">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={14} className="text-[#D4AF37]" />
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
                  Alan Leo · Key of Destiny — 7 life-arc phases
                </div>
              </div>
              <div className="text-[10px] text-[#6E6B64] font-mono mb-4">
                Click any phase to unlock Alan Leo's chart-scoped reading for that stretch of the native's life.
                Each phase is ruled by its planet (Moon → Mercury → Venus → Sun → Mars → Jupiter → Saturn).
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                {phases.map((p) => (
                  <button key={p.key} onClick={() => openPhase(p.key)}
                          data-testid={`alan-leo-phase-${p.key}`}
                          className={`text-left border px-3 py-2.5 rounded-sm transition-colors text-xs font-mono
                            ${phaseKey === p.key
                              ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                              : "border-[#2A2D35] text-[#A19E95] hover:border-[#D4AF37]/40 hover:text-[#E6E1D6]"}`}>
                    <div className="font-medium leading-tight">{p.label}</div>
                    <div className="text-[9px] text-[#6E6B64] mt-1">Ruler · {p.planet_ruler}</div>
                  </button>
                ))}
              </div>
              {phaseLoading && (
                <div className="mt-6 flex items-center gap-2 text-[#A19E95] font-mono text-sm">
                  <Loader2 size={14} className="animate-spin" /> Distilling this life-arc reading…
                </div>
              )}
              {phaseData && !phaseLoading && (
                <div className="mt-6 border border-[#D4AF37]/30 bg-[#0F1115] p-5"
                     data-testid={`alan-leo-reading-${phaseData.phase}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#A8896C] font-mono mb-1">
                    Ruler: {phaseData.planet_ruler}
                    {phaseData.phase_planet_condition?.sign && (
                      <span> · in {phaseData.phase_planet_condition.sign}
                        {phaseData.phase_planet_condition.house
                          ? ` · house ${phaseData.phase_planet_condition.house}`
                          : ""}
                        {phaseData.phase_planet_condition.state
                          ? ` · ${phaseData.phase_planet_condition.state}`
                          : ""}
                      </span>
                    )}
                  </div>
                  <div className="font-heading text-xl text-[#D4AF37] mb-4">{phaseData.label}</div>
                  {md(phaseData.narrative)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
